import { useState, useEffect } from 'react';
import { artistsApi } from '../../repositories/artists';
import { pitchesApi } from '../../repositories/pitches';
import { TrashIcon, PencilIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { Search, X, ArrowUp, ArrowDown } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import Pagination from '../../components/common/Pagination';
import PageHeader from '../../components/ui/PageHeader';
import { HOME_CRUMB } from '../../components/ui/Breadcrumbs';
import SegmentedTabs from '../../components/ui/SegmentedTabs';
import StatusBadge from '../../components/ui/StatusBadge';
import DataTable, { DataTableColumn } from '../../components/ui/DataTable';
import RowActionsMenu, { RowMenuItem, RowMenuLink } from '../../components/ui/RowActionsMenu';
import Swal from 'sweetalert2';
import { useAppSelector } from '../../hooks/redux';
import { adminApi } from '../../repositories/admin';
import { useDebounce } from 'use-debounce';
import { confirmDanger } from '../../utils/swal';

interface Artist {
  id: string;
  artistName: string;
  [key: string]: any;
}

interface Pitch {
  id: string;
  releaseTitle: string;
  releaseDate: string;
  [key: string]: any;
}

const AnalyticsList = () => {
  const { user } = useAppSelector((state) => state.auth);
  const isDsp = user?.role === 'dsp';
  // Drives StatusBadge's admin label variants, exactly as on /dashboard.
  const isAdmin = user?.role === 'admin';
  // The active tab is stored in the URL (?tab=release|artist) rather than component state so
  // it round-trips through browser history: clicking into an artist and hitting Back returns
  // to the Artist tab instead of snapping to the default. The URL is the single source of
  // truth - `activeTab` is derived from it, never held separately.
  const [searchParams, setSearchParams] = useSearchParams();
  // DSP users have no Release tab, so they're always on 'artist'. Everyone else defaults to
  // 'release' unless the param explicitly says 'artist'.
  const activeTab: 'release' | 'artist' =
    isDsp || searchParams.get('tab') === 'artist' ? 'artist' : 'release';
  const setActiveTab = (tab: 'release' | 'artist') => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      next.delete('page');
      return next;
    });
  };
  const [artists, setArtists] = useState<Artist[]>([]);
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [loading, setLoading] = useState(false);
  // Pagination lives in the URL (?page=) rather than component state, same reasoning as
  // `tab` above: it needs to survive a round trip through /analytics/detail/:id and back
  // (via the row action's "Analytics" link + browser Back / the "Analytics" breadcrumb), which
  // remounts this component and would otherwise always land back on page 1.
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const setPage = (next: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next <= 1) {
        params.delete('page');
      } else {
        params.set('page', String(next));
      }
      return params;
    });
  };
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 400);
  // sorting
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [releaseDateSortOrder, setReleaseDateSortOrder] = useState<'asc' | 'desc'>('desc');
  const [releaseNameSortOrder, setReleaseNameSortOrder] = useState<'asc' | 'desc'>('asc');

  // Normalise a missing/invalid ?tab= to the canonical value so /analytics and
  // /analytics?tab=release resolve to the same URL. replace:true keeps this off the history
  // stack, so it never interferes with the Back-button behaviour the param exists to fix.
  useEffect(() => {
    const raw = searchParams.get('tab');
    const canonical = isDsp ? 'artist' : raw === 'artist' ? 'artist' : 'release';
    if (raw !== canonical) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', canonical);
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, isDsp, setSearchParams]);

  useEffect(() => {
    if (activeTab === 'artist') {
      if (isDsp) {
        loadDspAssociatedArtists();
      } else {
        loadArtists();
      }
    } else if (activeTab === 'release') {
      loadPitches();
    }
  }, [activeTab, page, debouncedSearchTerm, isDsp]);

  const loadArtists = async () => {
    try {
      setLoading(true);
      const response = await artistsApi.getAll({
        page,
        limit: 10,
        search: debouncedSearchTerm
      });
      const artistsData = response.data.data?.artists || [];
      setArtists(artistsData);

      if (response.data.data?.pagination) {
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error loading artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDspAssociatedArtists = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getDspAssociatedArtists({
        page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
      });
      const artistsData = response.data.data?.artists.map((artist: any) => ({
        id: artist.artistUUID,
        artistName: artist.artistName,
        ...artist,
      })) || [];
      setArtists(artistsData);

      if (response.data.data?.pagination) {
        setTotalPages(response.data.data.pagination.totalPages);
      } else {
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error loading DSP associated artists:', error);
      setArtists([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const loadPitches = async () => {
    try {
      setLoading(true);
      const response = await pitchesApi.getAll({
        page,
        limit: 10,
        status: ['submitted'],
        search: debouncedSearchTerm,
        past: true,
        searchType: activeTab === 'release' ? 'release' : 'artist' 
      });

      // The response structure
      const pitchesData = response.data?.data || [];
      console.log('Pitches data:', pitchesData);

      setPitches(pitchesData);
      if (response.data?.pagination) {
        setTotalPages(response.data?.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error loading pitches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = () => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);

    const sortedArtists = [...artists].sort((a, b) => {
      const nameA = a.artistName.toLowerCase();
      const nameB = b.artistName.toLowerCase();

      if (newSortOrder === 'asc') {
        return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
      } else {
        return nameA > nameB ? -1 : nameA < nameB ? 1 : 0;
      }
    });

    setArtists(sortedArtists);
  };


  const handleDeleteArtist = async (artistId: string) => {
    const result = await confirmDanger({
      title: 'Are you sure?',
      text: 'This will permanently delete the artist.',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await artistsApi.delete(artistId);
        setArtists((prev) => prev.filter((artist) => artist.id !== artistId));
        Swal.fire('Deleted!', 'Artist has been deleted.', 'success');
      } catch (error: any) {
        console.error('Error deleting artist:', error);
        // The API explains *why* a delete was refused (e.g. only the user who
        // added an artist may remove it); show that instead of a generic line.
        Swal.fire('Error', error.response?.data?.message || 'Failed to delete artist.', 'error');
      }
    }
  };


  const handleSortByReleaseDate = () => {
    const newSortOrder = releaseDateSortOrder === 'desc' ? 'asc' : 'desc';
    setReleaseDateSortOrder(newSortOrder);

    const sortedPitches = [...pitches].sort((a, b) => {
      const dateA = new Date(a.releaseDate).getTime();
      const dateB = new Date(b.releaseDate).getTime();

      if (newSortOrder === 'desc') {
        return dateB - dateA; // Most recent first
      } else {
        return dateA - dateB; // Oldest first
      }
    });

    setPitches(sortedPitches);
  };

  const handleSortByReleaseName = () => {
    const newSortOrder = releaseNameSortOrder === 'asc' ? 'desc' : 'asc';
    setReleaseNameSortOrder(newSortOrder);

    const sortedPitches = [...pitches].sort((a, b) => {
      const nameA = a.releaseTitle.toLowerCase();
      const nameB = b.releaseTitle.toLowerCase();

      if (newSortOrder === 'asc') {
        return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
      } else {
        return nameA > nameB ? -1 : nameA < nameB ? 1 : 0;
      }
    });

    setPitches(sortedPitches);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setPage(1);
  };

  /*
   * Sortable column header. The arrows are lucide icons rather than the "↑"/"↓"
   * characters used before: those glyphs get emoji presentation in some
   * browsers, which is where the stray orange arrows came from.
   */
  const sortableHeader = (
    label: string,
    onClick: () => void,
    order: 'asc' | 'desc'
  ) => (
    <button
      type="button"
      onClick={onClick}
      className="border border-transparent inline-flex items-center gap-1.5 rounded transition-colors group hover:text-primary-700 focus:outline-none focus-visible:border-primary-700"
    >
      {label}
      {order === 'asc' ? (
        <ArrowUp className="w-3.5 h-3.5 text-neutral-400 group-hover:text-primary-700" />
      ) : (
        <ArrowDown className="w-3.5 h-3.5 text-neutral-400 group-hover:text-primary-700" />
      )}
    </button>
  );

  const artistColumns: DataTableColumn<any>[] = [
    {
      key: 'artistName',
      header: sortableHeader('Artist Name', handleSort, sortOrder),
      cell: (artist) => (
        <Link
          className="font-medium text-primary-700 hover:underline"
          to={`/artist/analytics-details/${artist.id}`}
        >
          {artist.artistName}
        </Link>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      cell: (artist) => (
        <RowActionsMenu label={`Actions for ${artist.artistName || 'artist'}`}>
          {!isDsp ? (
            <RowMenuItem
              tone="danger"
              icon={<TrashIcon className="w-4 h-4" />}
              onClick={() => handleDeleteArtist(artist.id)}
            >
              Delete
            </RowMenuItem>
          ) : (
            <RowMenuLink
              to={`/artist/analytics-details/${artist.id}`}
              icon={<PencilIcon className="w-4 h-4" />}
            >
              View Details
            </RowMenuLink>
          )}
        </RowActionsMenu>
      ),
    },
  ];

  const releaseColumns: DataTableColumn<any>[] = [
    {
      key: 'releaseTitle',
      header: sortableHeader('Release Name', handleSortByReleaseName, releaseNameSortOrder),
      cell: (pitch) => <span className="font-medium">{pitch.releaseTitle}</span>,
    },
    {
      key: 'releaseDate',
      header: sortableHeader('Release Date', handleSortByReleaseDate, releaseDateSortOrder),
      cell: (pitch) => (
        <span className="text-neutral-600">{formatDate(pitch.releaseDate)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      // Same StatusBadge as /dashboard and /pitch/submission/:id - the status
      // vocabulary here is identical, so the mapping is 1:1.
      cell: (pitch) =>
        pitch.status ? (
          <StatusBadge status={pitch.status} isAdmin={isAdmin} />
        ) : (
          'N/A'
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      // Same row-actions menu as the Artist tab — the two tabs of this page had
      // drifted into a lone button on one and a three-dot menu on the other.
      cell: (pitch) =>
        pitch.trackISRC ? (
          <RowActionsMenu label={`Actions for ${pitch.releaseTitle || 'release'}`}>
            <RowMenuLink
              to={`/analytics/detail/${pitch.id}?source=analytics&returnTo=${encodeURIComponent(
                `/analytics?${searchParams.toString()}`
              )}`}
              icon={<ChartBarIcon className="w-4 h-4" />}
            >
              Analytics
            </RowMenuLink>
          </RowActionsMenu>
        ) : null,
    },
  ];

  const tabItems = [
    ...(!isDsp ? [{ key: 'release', label: 'Release' }] : []),
    { key: 'artist', label: 'Artist' },
  ];

  return (
    <div className="py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "Analytics" }]}
        className="mb-6"
        title="Analytics"
        description="Streaming and playlist performance by release and by artist."
        actions={
          /* Search sits in the header's trailing slot on both tabs. */
          <div className="relative w-full sm:w-96">
            <Search className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={activeTab === 'release' ? 'Search by release name...' : 'Search by artist name...'}
              className="block w-full py-2 text-sm text-black bg-white border rounded-lg pl-9 pr-9 border-neutral-300 placeholder-neutral-400 focus:outline-none focus:border-primary-500"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                aria-label="Clear search"
                className="absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        }
      />

      {/* Search Results Info */}
      {searchTerm && (
        <div className="p-3 mb-4 border rounded-lg border-primary-200 bg-primary-50">
          <div className="flex items-center min-w-0">
            <Search className="w-4 h-4 mr-2 shrink-0 text-primary-700" />
            <span className="text-sm truncate text-primary-900">
              {activeTab === 'release'
                ? `Found ${pitches.length} result${pitches.length !== 1 ? 's' : ''} for "${searchTerm}" in release names`
                : `Found ${artists.length} result${artists.length !== 1 ? 's' : ''} for "${searchTerm}" in artist names`
              }
            </span>
          </div>
        </div>
      )}

      {/* Section tabs - same primitive as /dashboard's Pitches/Users tabs. */}
      <SegmentedTabs
        className="mb-4"
        ariaLabel="Analytics sections"
        value={activeTab}
        onChange={(key) => setActiveTab(key as 'release' | 'artist')}
        items={tabItems}
      />

      <div>
        {activeTab === "artist" ? (
          <div>
            <DataTable
              className="overflow-y-visible"
              columns={artistColumns}
              rows={artists}
              loading={loading}
              getRowKey={(artist: any) => artist.id}
              emptyMessage="No artists found."
            />
            <div className="mt-3">
              <Pagination
                page={page}
                totalPages={totalPages}
                setPage={setPage}
              />
            </div>
          </div>
        ) : activeTab === "release" ? (
          <div>
            <DataTable
              columns={releaseColumns}
              rows={pitches}
              loading={loading}
              getRowKey={(pitch: any) => pitch.id}
              emptyMessage="No pitches found."
            />
            <div className="mt-3">
              <Pagination
                page={page}
                totalPages={totalPages}
                setPage={setPage}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default AnalyticsList
